/**
 * Общая модель окна карточки площадки (ВБ, ОЗОН, Я.Маркет, Сайт).
 *
 * Каждое окно знает свою деталь (WB: `characteristic_specs`, OZ: `attribute_labels`,
 * YM: `parameter_values`), а каркас, проверки и вкладки у всех одни. Поэтому
 * площадочный файл сводит свою деталь к `McardModel`, а общий `McardWindow`
 * рисует всё остальное. Поле, которого площадка не даёт, остаётся `null`, а не
 * придумывается: окно покажет «нет данных» с причиной.
 */

import type { BadgeVariant } from '../../../../components/ui/Badge'
import type { IdMetaItem } from '../../../../components/ui/IdMeta'
import type { PresenceMarketplace } from '../../../products-v2/types'

export type McardPlatform = PresenceMarketplace

export const PLATFORM_KIND: Record<McardPlatform, string> = {
  wb: 'Карточка ВБ',
  ozon: 'Карточка ОЗОН',
  ym: 'Карточка Маркета',
  site: 'Карточка Сайта',
}

export const PLATFORM_NAME: Record<McardPlatform, string> = {
  wb: 'Wildberries',
  ozon: 'Ozon',
  ym: 'Яндекс Маркет',
  site: 'Сайт',
}

export const PLATFORM_SHORT: Record<McardPlatform, string> = {
  wb: 'ВБ',
  ozon: 'ОЗОН',
  ym: 'Маркет',
  site: 'Сайт',
}

export type McardIssueLevel = 'error' | 'warning'

export interface McardIssue {
  level: McardIssueLevel
  title: string
  text: string
}

export interface McardCharacteristic {
  /** Стабильный ключ строки. */
  key: string
  /** Идентификатор характеристики площадки; по нему сопоставляется ПИМ-значение. */
  targetId: number | null
  name: string
  required: boolean
  /** Что сейчас на витрине. */
  value: string
  /** Техническая пометка под полем: «WB ID 123 · до 5 знач.». */
  hint?: string
  /** Системное значение площадки: видно в окне, но не правится и не отправляется. */
  system?: { source: 'ozon_category_type'; lockedReason: string }
}

export interface McardPrice {
  /** Цена, которую видит покупатель. */
  shown: number | null
  /** Старая цена, зачёркнутая рядом. */
  old?: number | null
  /** Скидка в процентах. */
  discountPercent?: number | null
  currency?: string | null
}

/** Витрина: на ней карточка продаётся или нет. */
export interface McardShowcase {
  tone: 'ok' | 'warn' | 'bad'
  /** Явная видимость карточки: ошибки правки не всегда снимают её с витрины. */
  onShowcase?: boolean
  /** Значение чипа проверки: «на витрине», «в корзине». */
  value: string
  /** Если карточки на витрине нет — заголовок и пояснение вывода. */
  problem?: { title: string; hint: string }
}

export interface McardModel {
  platform: McardPlatform
  /** Артикул продавца: первичный идентификатор окна. */
  article: string
  title: string
  description: string
  brand: string | null
  statusLabel: string
  statusVariant: BadgeVariant
  showcase: McardShowcase
  photos: string[]
  /** Статус загрузки каждого фото на площадке (Я.Маркет). */
  photoStates?: Array<string | null>
  price: McardPrice
  stock: number | null
  /** Почему остатка нет: «нет источника». */
  stockWhy?: string
  issues: McardIssue[]
  characteristics: McardCharacteristic[]
  /** Справочник характеристик площадки загружен: иначе обязательность неизвестна. */
  specsKnown: boolean
  lastSyncAt: string | null
  platformUrl: string | null
  /** Служебные номера под заголовком (nmID, Ozon ID…); кабинет и штрихкод добавляет общий код. */
  idMeta: IdMetaItem[]
  barcode: string | null
  cabinetLabel: string
  designCode: string | null
  pimArticle: string | null
  themeNumber: string | number | null
  /** Габариты строкой для вкладки «Текст»; null — площадка их не прислала. */
  dimensionsText: string | null
  /** Категория/предмет для вкладки «Обзор». */
  categoryName: string | null
  /** Состояние синхронизации SEO-канала (ВБ/ОЗ): conflict, local_ahead. */
  seoSyncState?: string | null
  seoLastPublishedAt?: string | null
  /** Номер группы склейки на площадке (ВБ imt, ЯМ группа). */
  bundleExternalId?: string | number | null
  /** Подпись даты в футере: «Сверена с площадкой» (по умолчанию) или «Последний обмен». */
  lastSyncLabel?: string
  /** Проверка «Совпадает с ПИМ» не имеет смысла (Сайт: запись собирается из ПИМ при выгрузке). */
  pimCheck?: { tone: 'ok' | 'warn' | 'none'; value?: string; title?: string }
  /** Проверка «Характеристики» своя (Сайт: обязательных характеристик нет). */
  characteristicsCheck?: { tone: 'ok' | 'warn' | 'none'; value?: string; title?: string }
}

export const NO_DATA = 'нет данных'

export const formatRub = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return NO_DATA
  const number = Number(value)
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU')} ₽` : NO_DATA
}

export const plural = (count: number, forms: [string, string, string]): string => {
  const mod100 = Math.abs(count) % 100
  const mod10 = mod100 % 10
  if (mod100 >= 11 && mod100 <= 14) return forms[2]
  if (mod10 === 1) return forms[0]
  if (mod10 >= 2 && mod10 <= 4) return forms[1]
  return forms[2]
}

/** «3 поля», «1 поле», «5 полей» для бейджа расхождения. */
export const pluralFields = (count: number): string => plural(count, ['поле', 'поля', 'полей'])

/** Ссылка на окно: страница каталога с параметром окна, остальные параметры окон сброшены. */
export const cardWindowLink = (param: 'nm' | 'offer' | 'article', value: string): string => {
  try {
    const url = new URL(window.location.href)
    for (const key of ['nm', 'offer', 'article', 'card']) url.searchParams.delete(key)
    url.searchParams.set(param, value)
    return url.toString()
  } catch {
    return `?${param}=${encodeURIComponent(value)}`
  }
}

const numberText = (value: number): string =>
  value.toLocaleString('ru-RU', { maximumFractionDigits: 3 })

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const number = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'))
  return Number.isFinite(number) ? number : null
}

/**
 * Габариты строкой: «31 × 22 × 3 см, 0,12 кг». Единицы берутся из значения
 * (ОЗОН — мм и г), иначе см и кг. Нет ни размеров, ни веса — null.
 */
export const formatDimensions = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const sizes = [raw.length, raw.width, raw.height].map(finiteNumber)
  const weight = finiteNumber(raw.weight ?? raw.weightBrutto)
  const lengthUnit = typeof raw.length_unit === 'string' && raw.length_unit ? raw.length_unit : 'см'
  const weightUnit = typeof raw.weight_unit === 'string' && raw.weight_unit ? raw.weight_unit : 'кг'
  const parts: string[] = []
  if (sizes.some((size) => size !== null)) {
    parts.push(`${sizes.map((size) => (size === null ? '—' : numberText(size))).join(' × ')} ${lengthUnit}`)
  }
  if (weight !== null) parts.push(`${numberText(weight)} ${weightUnit}`)
  return parts.length > 0 ? parts.join(', ') : null
}

/** Значение характеристики ПИМ/витрины строкой: списки через запятую, пустое — пустая строка. */
export const characteristicText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map((item) => characteristicText(item)).filter(Boolean).join(', ')
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    if ('value' in record) return characteristicText(record.value)
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value)
}
